import { viewerSettingsService } from '@/lib/settings/viewerSettingsService';
import { useCallback, useEffect, useState } from 'react';
/**
 * Hook for managing viewer settings with automatic persistence
 */
export function useViewerSettings() {
    const [settings, setSettings] = useState(() => viewerSettingsService.getSettings());
    // Subscribe to settings changes
    useEffect(() => {
        const unsubscribe = viewerSettingsService.subscribe(() => {
            setSettings(viewerSettingsService.getSettings());
        });
        return unsubscribe;
    }, []);
    // ============================================================================
    // General
    // ============================================================================
    const updateSettings = useCallback((updates) => {
        viewerSettingsService.updateSettings(updates);
    }, []);
    const resetToDefaults = useCallback(() => {
        viewerSettingsService.resetToDefaults();
    }, []);
    // ============================================================================
    // Modality Layouts
    // ============================================================================
    const updateModalityLayout = useCallback((modality, layout) => {
        viewerSettingsService.setModalityLayout(modality, layout);
    }, []);
    const getLayoutForModality = useCallback((modality) => {
        return viewerSettingsService.getLayoutForModality(modality);
    }, []);
    // ============================================================================
    // Tool Bindings
    // ============================================================================
    const updateToolBindings = useCallback((bindings) => {
        viewerSettingsService.updateToolBindings(bindings);
    }, []);
    // ============================================================================
    // Overlay Settings
    // ============================================================================
    const updateOverlaySettings = useCallback((overlay, modality) => {
        viewerSettingsService.updateOverlaySettings(overlay, modality);
    }, []);
    // ============================================================================
    // W/L Presets
    // ============================================================================
    const togglePresetFavorite = useCallback((id) => {
        viewerSettingsService.togglePresetFavorite(id);
    }, []);
    const addCustomPreset = useCallback((preset) => {
        viewerSettingsService.addCustomPreset(preset);
    }, []);
    const removeCustomPreset = useCallback((id) => {
        viewerSettingsService.removeCustomPreset(id);
    }, []);
    const updateCustomPreset = useCallback((id, updates) => {
        viewerSettingsService.updateCustomPreset(id, updates);
    }, []);
    const getFavoritePresets = useCallback(() => {
        return viewerSettingsService.getFavoritePresets();
    }, []);
    const getPresetsForModality = useCallback((modality) => {
        return viewerSettingsService.getPresetsForModality(modality);
    }, []);
    // ============================================================================
    // Keyboard Shortcuts
    // ============================================================================
    const updateKeyboardShortcuts = useCallback((updates) => {
        viewerSettingsService.updateKeyboardShortcuts(updates);
    }, []);
    const resetKeyboardShortcuts = useCallback(() => {
        viewerSettingsService.resetKeyboardShortcuts();
    }, []);
    // ============================================================================
    // Audio Input (microphone)
    // ============================================================================
    const updateAudioInput = useCallback((updates) => {
        viewerSettingsService.updateAudioInput(updates);
    }, []);
    return {
        settings,
        // General
        updateSettings,
        resetToDefaults,
        // Layouts
        updateModalityLayout,
        getLayoutForModality,
        // Tool Bindings
        updateToolBindings,
        // Overlay Settings
        updateOverlaySettings,
        // W/L Presets
        togglePresetFavorite,
        addCustomPreset,
        removeCustomPreset,
        updateCustomPreset,
        getFavoritePresets,
        getPresetsForModality,
        // Keyboard Shortcuts
        updateKeyboardShortcuts,
        resetKeyboardShortcuts,
        // Audio Input
        updateAudioInput,
    };
}
